import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Select,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Divider,
  useToast,
  Progress,
  Icon,
  Flex
} from '@chakra-ui/react';
import { FiUpload, FiUser, FiMail, FiFileText, FiCheck, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useUser, useFindMany, useGlobalAction, useActionForm } from '@gadgetinc/react';
import { api } from '../api';

const ShiftSubmission = () => {
  const user = useUser(api);
  const toast = useToast();
  const navigate = useNavigate();
  
  // Form state
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [selectedCohort, setSelectedCohort] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isOBO, setIsOBO] = useState(false);
  const [oboEmail, setOboEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [studentUser, setStudentUser] = useState(null);

  // Fetch cohorts filtered by selected institution
  const [{ data: cohorts, fetching: cohortsLoading }] = useFindMany(api.cohort, {
    filter: selectedInstitution ? { institution: { equals: selectedInstitution } } : undefined,
    select: {
      id: true,
      name: true,
      displayName: true,
      institution: true,
      isActive: true
    }
  });

  // Fetch user by email for OBO functionality
  const [{ data: userByEmail, fetching: userByEmailLoading }] = useFindMany(api.user, {
    filter: oboEmail && oboEmail.includes('@') ? { email: { equals: oboEmail.toLowerCase().trim() } } : undefined,
    first: 1,
    select: {
      id: true,
      email: true,
      firstName: true,
      lastName: true,
      institution: true,
      cohort: {
        id: true,
        name: true,
        displayName: true,
        institution: true,
        isActive: true
      },
      roles: {
        key: true,
        name: true
      }
    }
  });

  // Global action for processing
  const [{ data: submitResult, fetching: processing, error: processError }, submitShiftManually] = useGlobalAction(api.submitShiftManually);

  // Check permissions - broadened to allow any user with appropriate roles
  const checkUserRoles = (userObj) => {
    if (!userObj?.roles) return false;
    return userObj.roles.some(role => {
      const roleKey = typeof role === 'string' ? role : role?.key || role?.name;
      return ['system-admin', 'admin', 'User', 'sysadmin'].includes(roleKey);
    });
  };

  const isSystemAdmin = user?.roles?.some(role => {
    const roleKey = typeof role === 'string' ? role : role?.key || role?.name;
    return ['system-admin', 'sysadmin'].includes(roleKey);
  });

  const isAdmin = user?.roles?.some(role => {
    const roleKey = typeof role === 'string' ? role : role?.key || role?.name;
    return roleKey === 'admin';
  });

  const isUser = user?.roles?.some(role => {
    const roleKey = typeof role === 'string' ? role : role?.key || role?.name;
    return roleKey === 'User';
  });

  // Debug logging
  useEffect(() => {
    console.log('=== Shift Submission Access Debug ===');
    console.log('User:', user?.email);
    console.log('User roles:', user?.roles);
    console.log('User institution:', user?.institution);
    console.log('isSystemAdmin:', isSystemAdmin);
    console.log('isAdmin:', isAdmin);
    console.log('isUser:', isUser);
    console.log('checkUserRoles result:', checkUserRoles(user));
  }, [user, isSystemAdmin, isAdmin, isUser]);

  // Get user's institutions - improved logic
  const userInstitutions = useMemo(() => {
    if (isSystemAdmin) {
      // System admin can access any institution
      return [
        'ElevationEMS', 
        'STCC', 
        'Southwest Tennessee Community College',
        'Southwest TN Community College',
        'Elevation EMS',
        'Test Institution'
      ];
    }
    
    // Try multiple fields to find institution info
    let institutions = [];
    
    if (user?.institution) {
      institutions = Array.isArray(user.institution) ? user.institution : [user.institution];
    }
    
    // If no institution found but user is admin or User role, provide common options
    if (institutions.length === 0 && (isAdmin || isUser)) {
      institutions = [
        'ElevationEMS', 
        'STCC', 
        'Southwest Tennessee Community College'
      ];
    }
    
    return institutions;
  }, [user, isSystemAdmin, isAdmin, isUser]);

  // Auto-population function for institution and cohort
  const autoPopulateFields = (targetUser, isOBOMode = false) => {
    console.log('Auto-populating fields for user:', targetUser?.email, 'OBO mode:', isOBOMode);
    
    if (!targetUser) return;

    // Auto-populate institution
    let institutionToSet = '';
    if (targetUser.institution) {
      institutionToSet = Array.isArray(targetUser.institution) 
        ? targetUser.institution[0] 
        : targetUser.institution;
    } else if (targetUser.cohort?.institution) {
      institutionToSet = targetUser.cohort.institution;
    }
    
    if (institutionToSet && institutionToSet !== selectedInstitution) {
      console.log('Setting institution to:', institutionToSet);
      setSelectedInstitution(institutionToSet);
    }

    // Auto-populate cohort if user has an active cohort
    if (targetUser.cohort?.id && targetUser.cohort?.isActive !== false) {
      console.log('Setting cohort to:', targetUser.cohort.id);
      setSelectedCohort(targetUser.cohort.id);
    }
  };

  // Auto-select institution if user has only one
  useEffect(() => {
    if (userInstitutions.length === 1 && !selectedInstitution && !isOBO) {
      setSelectedInstitution(userInstitutions[0]);
    }
  }, [userInstitutions, selectedInstitution, isOBO]);

  // Auto-populate current user's info when not in OBO mode
  useEffect(() => {
    if (!isOBO && user && !selectedInstitution) {
      autoPopulateFields(user, false);
    }
  }, [user, isOBO, selectedInstitution]);

  // Auto-populate student info when in OBO mode and email is found
  useEffect(() => {
    if (isOBO && userByEmail && userByEmail.length > 0) {
      const foundUser = userByEmail[0];
      console.log('Found student user:', foundUser);
      setStudentUser(foundUser);
      autoPopulateFields(foundUser, true);
    } else if (isOBO && oboEmail && !userByEmailLoading && (!userByEmail || userByEmail.length === 0)) {
      // Clear fields if no user found with that email
      console.log('No user found for email:', oboEmail);
      setStudentUser(null);
    }
  }, [isOBO, userByEmail, userByEmailLoading, oboEmail]);

  // Auto-select cohort if only one available in selected institution
  useEffect(() => {
    if (cohorts && cohorts.length === 1 && !selectedCohort && selectedInstitution) {
      setSelectedCohort(cohorts[0].id);
    }
  }, [cohorts, selectedCohort, selectedInstitution]);

  // Reset fields when switching OBO mode
  useEffect(() => {
    if (!isOBO) {
      setOboEmail('');
      setStudentUser(null);
      // Re-populate with current user's info
      autoPopulateFields(user, false);
    } else {
      // Clear fields when entering OBO mode
      setSelectedInstitution('');
      setSelectedCohort('');
    }
  }, [isOBO, user]);

  // Check if user has access - broadened permissions
  const hasAccess = checkUserRoles(user);

  // Validate file
  const validateFile = (file) => {
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!validTypes.includes(file.type)) {
      return 'Please upload a PDF, Word document, or image file.';
    }
    
    if (file.size > maxSize) {
      return 'File size must be less than 10MB.';
    }
    
    return null;
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        setErrors({ ...errors, file: error });
        return;
      }
      setSelectedFile(file);
      setErrors({ ...errors, file: null });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedInstitution) {
      newErrors.institution = 'Please select an institution.';
    }
    
    if (!selectedCohort) {
      newErrors.cohort = 'Please select a cohort.';
    }
    
    if (!shiftId.trim()) {
      newErrors.shiftId = 'Shift ID is required.';
    }
    
    if (!selectedFile) {
      newErrors.file = 'Please upload a document.';
    }
    
    if (isOBO && !oboEmail.trim()) {
      newErrors.oboEmail = 'Student email is required when submitting on behalf of student.';
    }
    
    if (isOBO && oboEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(oboEmail)) {
      newErrors.oboEmail = 'Please enter a valid email address.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create file object for submission
      const fileObject = {
        filename: selectedFile.name,
        mimetype: selectedFile.type,
        size: selectedFile.size,
        url: selectedFile.name // This will be handled by the action
      };
      
      // Prepare submission data
      const submissionData = {
        institution: selectedInstitution,
        cohortId: selectedCohort,
        shiftId: shiftId.trim(),
        submissionEmail: isOBO ? oboEmail.trim() : user.email,
        file: fileObject,
        isOBO: isOBO,
        originalSubmitter: isOBO ? user.email : undefined
      };
      
      // Submit the shift using the submitShiftManually global action
      const result = await submitShiftManually(submissionData);
      
      // Handle the response
      if (result?.success) {
        setSubmitSuccess(true);
        toast({
          title: 'Shift Submitted Successfully',
          description: `Shift ${shiftId} has been submitted for processing. ${isOBO ? 'Notifications will be sent to the student.' : ''}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Reset form
        setShiftId('');
        setSelectedFile(null);
        setIsOBO(false);
        setOboEmail('');
        setErrors({});
      } else {
        // Handle action errors
        const errorMessage = result?.errors?.map(e => e.message).join(', ') || 'Submission failed';
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: 'Submission Failed',
        description: error.message || 'An error occurred while submitting the shift. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (!user) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  // Access denied
  if (!hasAccess) {
    return (
      <Box p={8}>
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to access this page. This page requires admin, User, or system-admin roles.
          </AlertDescription>
        </Alert>
        
        {/* Debug information */}
        <Box mt={4} p={4} bg="gray.100" borderRadius="md">
          <Text fontWeight="bold" mb={2}>Debug Information:</Text>
          <Text fontSize="sm">Email: {user?.email}</Text>
          <Text fontSize="sm">Roles: {JSON.stringify(user?.roles)}</Text>
          <Text fontSize="sm">Institution: {user?.institution}</Text>
          <Text fontSize="sm">Has Access: {hasAccess ? 'Yes' : 'No'}</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxW="4xl" mx="auto" p={4}>
      {/* Back to Admin Button */}
      <Box mb={4}>
        <Button
          leftIcon={<Icon as={FiArrowLeft} />}
          variant="ghost"
          colorScheme="blue"
          onClick={() => navigate('/admin')}
          size="sm"
        >
          Back to Admin
        </Button>
      </Box>
      
      <Card>
        <CardHeader>
          <Heading size="lg" color="blue.600">
            <Icon as={FiFileText} mr={3} />
            Manual Shift Submission
          </Heading>
          <Text color="gray.600" mt={2}>
            Submit student shifts for AI processing and evaluation
          </Text>
        </CardHeader>
        
        <CardBody>
          <VStack spacing={6} align="stretch">
            
            {/* Success Message */}
            {submitSuccess && (
              <Alert status="success">
                <AlertIcon />
                <AlertTitle>Submission Successful!</AlertTitle>
                <AlertDescription>
                  The shift has been submitted and will be processed through our AI evaluation system.
                </AlertDescription>
              </Alert>
            )}

            {/* Institution Selection */}
            <FormControl isInvalid={errors.institution}>
              <FormLabel>
                <Icon as={FiUser} mr={2} />
                Institution
              </FormLabel>
              <Select
                placeholder="Select Institution..."
                value={selectedInstitution}
                onChange={(e) => {
                  setSelectedInstitution(e.target.value);
                  setSelectedCohort(''); // Reset cohort when institution changes
                }}
                isDisabled={userInstitutions.length === 1 && !isSystemAdmin}
                bg={userInstitutions.length === 1 && !isSystemAdmin ? "gray.100" : "white"}
              >
                {userInstitutions.map((institution) => (
                  <option key={institution} value={institution}>
                    {institution}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.institution}</FormErrorMessage>
            </FormControl>

            {/* Cohort Selection */}
            <FormControl isInvalid={errors.cohort}>
              <FormLabel>Cohort</FormLabel>
              <Select
                placeholder="Select Cohort..."
                value={selectedCohort}
                onChange={(e) => setSelectedCohort(e.target.value)}
                isDisabled={!selectedInstitution || cohortsLoading || (cohorts?.length === 1)}
                bg={cohorts?.length === 1 ? "gray.100" : "white"}
              >
                {cohorts?.map((cohort) => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.displayName || cohort.name}
                  </option>
                ))}
              </Select>
              {cohortsLoading && <Text fontSize="sm" color="gray.500">Loading cohorts...</Text>}
              <FormErrorMessage>{errors.cohort}</FormErrorMessage>
            </FormControl>

            <Divider />

            {/* Shift ID */}
            <FormControl isInvalid={errors.shiftId}>
              <FormLabel>Shift ID</FormLabel>
              <Input
                placeholder="Enter shift ID (e.g., FISDAP-12345)"
                value={shiftId}
                onChange={(e) => setShiftId(e.target.value)}
              />
              <FormErrorMessage>{errors.shiftId}</FormErrorMessage>
            </FormControl>

            {/* File Upload */}
            <FormControl isInvalid={errors.file}>
              <FormLabel>
                <Icon as={FiUpload} mr={2} />
                Document Upload
              </FormLabel>
              <Box
                border="2px dashed"
                borderColor={selectedFile ? "green.200" : "gray.200"}
                borderRadius="md"
                p={6}
                textAlign="center"
                bg={selectedFile ? "green.50" : "gray.50"}
                _hover={{ borderColor: "blue.300", bg: "blue.50" }}
                cursor="pointer"
                onClick={() => document.getElementById('file-input').click()}
              >
                <Input
                  id="file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  onChange={handleFileChange}
                  display="none"
                />
                <Icon as={selectedFile ? FiCheck : FiUpload} size="xl" mb={2} />
                <Text>
                  {selectedFile ? selectedFile.name : "Click to upload or drag and drop"}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  PDF, Word documents, or images (max 10MB)
                </Text>
              </Box>
              <FormErrorMessage>{errors.file}</FormErrorMessage>
            </FormControl>

            <Divider />

            {/* Submit on Behalf Of */}
            <Box>
              <Checkbox
                isChecked={isOBO}
                onChange={(e) => {
                  setIsOBO(e.target.checked);
                  if (!e.target.checked) {
                    setOboEmail('');
                    setStudentUser(null);
                    setErrors({ ...errors, oboEmail: null });
                  }
                }}
              >
                <Text fontWeight="medium">Submit on behalf of student</Text>
              </Checkbox>
              <Text fontSize="sm" color="gray.600" mt={1}>
                Check this box if you're submitting this shift for a student
              </Text>
              
              {isOBO && (
                <VStack spacing={4} align="stretch">
                  <FormControl mt={4} isInvalid={errors.oboEmail}>
                    <FormLabel>
                      <Icon as={FiMail} mr={2} />
                      Student Email
                    </FormLabel>
                    <Input
                      type="email"
                      placeholder="student@example.com"
                      value={oboEmail}
                      onChange={(e) => setOboEmail(e.target.value)}
                    />
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      Enter the student's email to auto-populate their institution and cohort
                    </Text>
                    <FormErrorMessage>{errors.oboEmail}</FormErrorMessage>
                  </FormControl>

                  {/* Student lookup status */}
                  {oboEmail && oboEmail.includes('@') && (
                    <Box>
                      {userByEmailLoading ? (
                        <HStack>
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.500">Looking up student...</Text>
                        </HStack>
                      ) : studentUser ? (
                        <Alert status="success" size="sm" borderRadius="md">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Found student: {studentUser.firstName} {studentUser.lastName}
                            {studentUser.cohort && ` (${studentUser.cohort.displayName || studentUser.cohort.name})`}
                          </Text>
                        </Alert>
                      ) : (
                        <Alert status="warning" size="sm" borderRadius="md">
                          <AlertIcon />
                          <Text fontSize="sm">
                            No student found with email: {oboEmail}
                          </Text>
                        </Alert>
                      )}
                    </Box>
                  )}
                </VStack>
              )}
            </Box>

            {/* Submit Button */}
            <Button
              colorScheme="blue"
              size="lg"
              onClick={handleSubmit}
              isLoading={isSubmitting || processing}
              loadingText="Submitting..."
              leftIcon={<Icon as={FiFileText} />}
              isDisabled={!selectedInstitution || !selectedCohort || !shiftId || !selectedFile}
            >
              Submit Shift for Processing
            </Button>

            {/* Processing Status */}
            {processing && (
              <Box>
                <Text fontSize="sm" mb={2}>Processing submission...</Text>
                <Progress size="sm" isIndeterminate colorScheme="blue" />
              </Box>
            )}

            {/* Error Display */}
            {processError && (
              <Alert status="error">
                <AlertIcon />
                <AlertTitle>Submission Error</AlertTitle>
                <AlertDescription>
                  {processError.message || 'An error occurred during submission. Please try again.'}
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default ShiftSubmission;